import { collection, doc, getDoc, getDocs, updateDoc, setDoc, arrayUnion, arrayRemove, Timestamp as FirebaseTimestamp, serverTimestamp as FirebaseServerTimestamp } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { ref } from 'firebase/storage';
import { Tenancy } from '@/contexts/TenancyContext';
import { UserOption } from '@/types/user';
import { PrivacyRequestWithId } from '@/services/privacyRequestTrackerV2';

export interface PrivacyRequestsItem {
  id?: number;
  email: string;
  name: string;
  phoneNumber: string;
  requestDate?: any;
  requestID?: number;
  status?: string;
  typeOfRequest: string;

  declined?: boolean;
  fulfilled?: boolean;

  notes?: PrivacyRequestNote[];

  /* Computed fields */
  security?: 'public' | 'private';
}

export interface PrivacyRequestNote {
  body: string;
  metadata: PrivacyRequestNoteMetadata;
}

export interface PrivacyRequestNoteMetadata {
  author: PrivacyRequestNoteAuthor;
  date: FirebaseTimestamp;
}

export interface PrivacyRequestNoteAuthor {
  id: string;
  name: string;
}

export function createEmptyPrivacyRequestsItem(): PrivacyRequestWithId {
  // Only return the fields required for the user to fill out
  // @ts-ignore
  return {
    name: '',
    phone: '',
    email: '',
    message: '',
  };
}

export async function getPrivacyRequestList(companyID: string) {
  const privateDocRef = doc(db, 'customers', companyID, 'compliance', 'privacyRequestsTracker');
  const publicDocRef = doc(db, 'privacyRequestTracker', companyID);

  const [ privateSnapshot, publicSnapshot ] = await Promise.all([
    await getDoc(privateDocRef),
    await getDoc(publicDocRef),
  ]);

  const privateData = privateSnapshot.exists() ? privateSnapshot.data() : null;
  const publicData = publicSnapshot.exists() ? publicSnapshot.data() : null;

  if (!privateData && !publicData) {
    console.warn(
      '[privacyRequestTracker]',
      'getPrivacyRequestList',
      'privacyRequestsTracker document does not exist or contains no data!'
    );
    return [];
  }

  const privateRequests: PrivacyRequestsItem[] = privateData
    ? Object.entries(privateData).map(([key, item]) => ({
      id: parseInt(key, 10),
      ...item,
      security: 'private',
    }))
    : [];

  const publicRequests: PrivacyRequestsItem[] = publicData
    ? Object.entries(publicData).map(([key, item]) => ({
      id: parseInt(key, 10),
      ...item,
      security: 'public',
    }))
    : [];

  let requests = [...privateRequests, ...publicRequests];
  requests = requests.filter(req => req.deleted === false || req.deleted == null);

  console.log('[privacyRequestTracker]', 'getPrivacyRequestList', companyID, privateRequests, publicRequests);
  return requests;
}

export async function addPrivacyRequestNote(companyID: string, privacyRequestItem: PrivacyRequestsItem, newNote: PrivacyRequestNote) {
  // const requestDocRef = doc(db, 'customers', companyID, 'compliance', 'privacyRequestsTracker');

  const requestDocRef = privacyRequestItem.security === 'public' ? doc(db, 'privacyRequestTracker', companyID) : doc(db, 'customers', companyID, 'compliance', 'privacyRequestsTracker');

  // Check to see if the `privacyRequestsTracker` doc exists before trying to write to it
  const snapshot = await getDoc(requestDocRef);
  if (!snapshot.exists()) {
    console.log('[privacyRequestTracker]', 'addPrivacyRequestNote', 'privacyRequestsTracker doc doesnt exist! Creating it now.');

    await setDoc(requestDocRef, {});
  }

  const itemKey = privacyRequestItem.id.toString();
  const notesPath = `${itemKey}.notes`;

  console.log(`[privacyRequestTracker]`, requestDocRef, itemKey, notesPath, snapshot.data(), newNote);

  try {
    await updateDoc(requestDocRef, {
      [notesPath]: arrayUnion(newNote),
    });

    return true;
  } catch (error) {
    console.log(error);
  }

  return false;
}
export async function delPrivacyRequestNote(company: Tenancy, privacyRequestItem: PrivacyRequestsItem, note: PrivacyRequestNote) {
  const docRef = privacyRequestItem.security === 'public' ? doc(db, 'privacyRequestTracker', company.id) : doc(db, 'customers', company.id, 'compliance', 'privacyRequestsTracker');

  try {
    await updateDoc(docRef, {
      [`${privacyRequestItem.id}.notes`]: arrayRemove(note),
    });
  } catch (error) {
    console.log(error);
    return false;
  }

  return true;
}

export async function createPrivacyRequestItem(company: Tenancy, user: User, data) {
  const docRef = doc(db, 'customers', company.id, 'compliance', 'privacyRequestsTracker');

  // Get all current and find the highest request id
  const currentDocs = await getPrivacyRequestList(company.id);

  // Handle empty or undefined currentDocs (new customers)
  const existingIDs = (currentDocs || [])
    .map(item => item?.requestID)
    .filter((id): id is number => typeof id === 'number' && !isNaN(id));
  const highestRequestID = existingIDs.length > 0 ? Math.max(...existingIDs) : 0;
  const newRequestID = highestRequestID + 1;

  const newItem = {
    name: data.name,
    email: data.email,
    phoneNumber: data.phoneNumber,
    requestID: newRequestID,
    requestedDate: FirebaseServerTimestamp(),
    typeOfRequest: data.typeOfRequest,
    status: 'Open',
    metadata: {
      dateCreated: FirebaseServerTimestamp(),
      id: user.uid,
      name: user.displayName,
    }
  }
  console.log('[privacyRequestTracker]', 'createPrivacyRequestItem', newItem);

  try {
    await setDoc(docRef, { [`${newRequestID}`]: { ...newItem } }, { merge: true });
  } catch (error) {
    console.error('[privacyRequestTracker] Failed to create privacy request item:', error);
    return error;
  }

  // TODO: Fire notification

  return true;
}

export async function deletePrivacyRequestItem(company: Tenancy, privacyRequestItem: PrivacyRequestsItem) {
  const docRef = privacyRequestItem.security === 'public'
    ? doc(db, 'privacyRequestTracker', company.id)
    : doc(db, 'customers', company.id, 'compliance', 'privacyRequestsTracker');

  try {
    await updateDoc(
      docRef,
      {
        [`${privacyRequestItem.requestID}.deleted`]: true
      },
      { merge: true }
    );
  } catch (error) {
    console.error('[deletePrivacyRequestItem]', error);
    return error;
  }

  return true;
}

export async function changePrivacyRequestItemStatus(company: Tenancy, user: UserOption, privacyRequestItem: PrivacyRequestsItem, newstatus: string) {
  const docRef = privacyRequestItem.security === 'public' ? doc(db, 'privacyRequestTracker', company.id) : doc(db, 'customers', company.id, 'compliance', 'privacyRequestsTracker');

  console.log('[changePrivacyRequestItemStatus]', company, user, privacyRequestItem, newstatus);

  try {
    await updateDoc(docRef, {
      [`${privacyRequestItem.requestID}.status`]: newstatus
    }, { merge: true });

  } catch (error) {
    console.error('[changePrivacyRequestItemStatus]', error);
    return error;
  }

  return true;
}

export async function updatePrivacyRequestItem(company: Tenancy, user: UserOption, privacyRequestItem: PrivacyRequestsItem) {
  const docRef = privacyRequestItem.security === 'public' ? doc(db, 'privacyRequestTracker', company.id) : doc(db, 'customers', company.id, 'compliance', 'privacyRequestsTracker');

  console.log('[updatePrivacyRequestItem]', company, user, updatePrivacyRequestItem);

  try {
    await updateDoc(docRef, {
      [`${privacyRequestItem.requestID}`]: privacyRequestItem
    }, { merge: true });

  } catch (error) {
    console.error('[changePrivacyRequestItemStatus]', error);
    return error;
  }

  return true;
}
